import * as y from 'yup'

import { EMAIL_DOMAIN_NAME, EMAIL_PATTERN } from '@src/constants/SignUpSchema'
import { countryCodeISOMap } from '@src/i18n/countries'
import type { CountryCode } from '@src/i18n/locales'
import { lookupPostalCode } from '@src/utils/address-search.util'
import { isCardExpirationValidTest } from '@src/utils/isCardExpirationValidTest.util'

export const NAME_PATTERN =
  /^[A-Za-z.‘’'\s\-äòõöšýžáíìóúñńàâçéèêëîïôœùûüÿÁÍÓÚÑÀÂÇÉÈÊËÎÏÔŒÙÛÜŸÃÄÌÒÕÖÝŽ]+$/

export const ADDRESS_PATTERN =
  /^[A-Za-z0-9 #.\-‘’'äòõöšýžáíìóúñńàâçéèêëîïôœùûüÿÁÍÓÚÑÀÂÇÉÈÊËÎÏÔŒÙÛÜŸÃÄÌÒÕÖÝŽ]+$|^$/

export const CITY_PATTERN =
  /^[A-Za-z.\d\s\-&‘’'äòõöšýžáíìóúñńàâçéèêëîïôœùûüÿÁÍÓÚÑÀÂÇÉÈÊËÎÏÔŒÙÛÜŸÃÄÌÒÕÖÝŽ]+$/

export const NON_SPACE_STRING_PATTERN = /\S+/

export const CC_EXP_PATTERN = /^(0[1-9]|1[0-2])\/?([0-9]{2})$/

export const US_POSTAL_CODE_PATTERN = /^\d{5}(-\d{4})?$/
export const GB_POSTAL_CODE_PATTERN = /^[A-Za-z]{1,2}\d[A-Za-z\d]?\s*\d[A-Za-z]{2}$/
export const CA_POSTAL_CODE_PATTERN =
  /^(?!.*[DFIOQUdfioqu])[A-VXYa-vxy][0-9][A-Za-z] ?[0-9][A-Za-z][0-9]$/

export const POSTAL_CODE_PATTERNS: Record<CountryCode, RegExp> = {
  US: US_POSTAL_CODE_PATTERN,
  CA: CA_POSTAL_CODE_PATTERN,
  GB: GB_POSTAL_CODE_PATTERN,
}

const addressFields = {
  address: y.string(),
  addressNumber: y.string(),
  city: y.string(),
  state: y.string(),
  zip: y.string(),
}

export const getAddressFieldsForLocale = (
  country: CountryCode,
  addressCategory: 'billingAddress' | 'shippingAddress'
) => {
  return {
    address: y
      .string()
      .required('address.required')
      .matches(ADDRESS_PATTERN, 'address.invalid')
      .max(30, 'address.invalid'),
    addressNumber: y
      .string()
      .matches(ADDRESS_PATTERN, 'addressNumber.invalid')
      .max(30, 'addressNumber.invalid'),
    city: y.string().matches(CITY_PATTERN, 'city.invalid').required('city.required'),
    state: y.string().required('state.required'),
    zip: y
      .string()
      .required('zip.required')
      .matches(POSTAL_CODE_PATTERNS[country] ?? US_POSTAL_CODE_PATTERN, 'zip.invalid')
      .min(5, 'zip.invalid')
      .max(10, 'zip.invalid')
      .test({
        name: 'is-zip-valid',
        message: 'zip.invalid',
        test: async (value, { createError }) => {
          try {
            const countryCodeIso = countryCodeISOMap[country]
            const suggestions = await lookupPostalCode({
              postalCode: value,
              countryIsoCode: countryCodeIso,
            })
            if (!suggestions?.length) {
              return createError({ path: `${addressCategory}.zip`, message: 'zip.invalid' })
            }
            return true
          } catch (e) {
            return createError({ path: `${addressCategory}.zip`, message: 'zip.invalid' })
          }
        },
      }),
  }
}

export const shippableSchema = (country: CountryCode) => {
  return y.object({
    checkoutType: y
      .string()
      .oneOf(['credit-card', 'apple-pay'] as const)
      .required('checkoutType.required'),
    email: y.string().when('$guestCheckoutEnabled', ([guestCheckoutEnabled], schema) => {
      if (!guestCheckoutEnabled) return schema
      return schema
        .required('email.invalid')
        .max(50, 'email.invalid')
        .matches(EMAIL_PATTERN, 'email.invalid')
        .matches(EMAIL_DOMAIN_NAME, 'email.invalid')
    }),
    disclaimer: y.bool().oneOf([true], 'disclaimer.required'),
    partner: y.string(),
    deliveryMethod: y.string(),
    isBillingAddressSameAsShipping: y.boolean(),
    firstName: y.string().when('checkoutType', {
      is: (checkoutType: string) => checkoutType === 'credit-card',
      then: (schema) =>
        schema
          .required('firstName.required')
          .matches(NON_SPACE_STRING_PATTERN, 'firstName.invalid')
          .matches(NAME_PATTERN, 'firstName.invalid')
          .max(30, 'firstName.invalid'),
    }),
    lastName: y.string().when('checkoutType', {
      is: (checkoutType: string) => checkoutType === 'credit-card',
      then: (schema) =>
        schema
          .required('lastName.required')
          .max(30, 'lastName.invalid')
          .matches(NON_SPACE_STRING_PATTERN, 'lastName.invalid')
          .matches(NAME_PATTERN, 'lastName.invalid'),
    }),
    shippingAddress: y.object(addressFields).when('checkoutType', {
      is: (checkoutType: string) => checkoutType === 'credit-card',
      then: (schema) => schema.shape(getAddressFieldsForLocale(country, 'shippingAddress')),
    }),
    billingAddress: y
      .object(addressFields)
      .when(['isBillingAddressSameAsShipping', 'checkoutType'], {
        is: (isBillingAddressSameAsShipping: boolean, checkoutType: string) =>
          !isBillingAddressSameAsShipping && checkoutType === 'credit-card',
        then: (schema) => schema.shape(getAddressFieldsForLocale(country, 'billingAddress')),
      }),
    cardFirstName: y.string().when('checkoutType', {
      is: (checkoutType: string) => checkoutType === 'credit-card',
      then: (schema) =>
        schema
          .required('cardFirstName.required')
          .matches(NON_SPACE_STRING_PATTERN, 'firstName.invalid')
          .matches(NAME_PATTERN, 'cardFirstName.invalid')
          .max(30, 'cardFirstName.invalid'),
    }),
    cardLastName: y.string().when('checkoutType', {
      is: (checkoutType: string) => checkoutType === 'credit-card',
      then: (schema) =>
        schema
          .required('cardLastName.required')
          .matches(NON_SPACE_STRING_PATTERN, 'lastName.invalid')
          .matches(NAME_PATTERN, 'cardLastName.invalid')
          .max(30, 'cardLastName.invalid'),
    }),
    cardExpiration: y.string().when('checkoutType', {
      is: (checkoutType: string) => checkoutType === 'credit-card',
      then: (schema) =>
        schema
          .required('cardExpiration.required')
          .matches(CC_EXP_PATTERN, 'cardExpiration.invalid')
          .test('is-valid-date', 'cardExpiration.invalid', isCardExpirationValidTest),
    }),
    cardType: y.string(),
    cardToken: y.string().when(['checkoutType', '$showSavedPayments'], {
      is: (checkoutType: string, showSavedPayments: boolean) =>
        checkoutType === 'credit-card' && !showSavedPayments,
      then: (schema) => schema.required('cardToken.required'),
    }),
    instrumentIdentifier: y.string().when(['checkoutType', '$showSavedPayments'], {
      is: (checkoutType: string, showSavedPayments: boolean) =>
        checkoutType === 'credit-card' && showSavedPayments,
      then: (schema) => schema.required('Instrument Identifier is required'),
    }),
    applePayToken: y.string(),
  })
}

export type ShippableCheckoutSchema = y.InferType<ReturnType<typeof shippableSchema>>
